// PROTOTYPE: This file was scaffolded quickly and is pending a rewrite. Do not use as a model for other parts of the codebase.
import { useCallback, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { AlertCircle } from 'lucide-react'
import { toast } from 'sonner'
import {
    ResizablePanelGroup,
    ResizablePanel,
    ResizableHandle,
} from '@/components/ui/resizable'
import type { AdPlatform, MetaAd } from '@storefronts/common-types'
import { AD_PLATFORM } from '@storefronts/common-types'
import { R } from '@/modules/api/actions'
import { useAIChatContext } from './AIChatContext'
import { ChatPanel } from './ChatPanel'
import { ContentPanel } from './ContentPanel'
import {
    AiCreateRunSignalsProvider,
    useAiCreateRunSignals,
} from './AiCreateRunSignals'
import { AICreateRuntimeProvider } from './AICreateRuntimeProvider'
import { isPreviewReady } from './useActivePreview'
import {
    AiCreateSessionFeed,
    useAIChatOrchestrator,
} from './useAIChatOrchestrator'
import { PrefaceComposer, type PrefaceSubmission } from './PrefaceComposer'
import { useSourceStorefrontSeed } from './useSourceStorefrontSeed'

// Creative hand-off links (e.g. the Paid Funnel Report's "Generate
// Storefront" action) carry the channel as a lowercase string param. Only
// Meta actually has a creative-by-id resolver wired up below; unrecognized or
// not-yet-supported channels fall back to Meta so old/malformed links keep
// working exactly as before this param existed.
const CHANNEL_PARAM_TO_AD_PLATFORM: Partial<Record<string, AdPlatform>> = {
    meta: AD_PLATFORM.META,
    google: AD_PLATFORM.GOOGLE,
    tiktok: AD_PLATFORM.TIKTOK,
    pinterest: AD_PLATFORM.PINTEREST,
    snapchat: AD_PLATFORM.SNAPCHAT,
    axon: AD_PLATFORM.AXON,
    rokt: AD_PLATFORM.ROKT,
}

function AiCreateChatLayout() {
    const { state } = useAIChatContext()
    const { contentReady } = useAiCreateRunSignals()
    const showPreview =
        !!state.snapshot?.finalLandingPageHash &&
        isPreviewReady(state.snapshot, state.isComplete, contentReady)

    return (
        <ResizablePanelGroup direction="horizontal">
            <ResizablePanel defaultSize={45} minSize={30}>
                <ChatPanel />
            </ResizablePanel>
            {showPreview ? (
                <>
                    <ResizableHandle withHandle className="bg-transparent" />
                    <ResizablePanel defaultSize={55} minSize={30}>
                        <div className="h-full py-3 pr-3">
                            <div className="h-full overflow-hidden rounded-xl border shadow-sm bg-background border-border">
                                <ContentPanel />
                            </div>
                        </div>
                    </ResizablePanel>
                </>
            ) : null}
        </ResizablePanelGroup>
    )
}

function parseChannelParam(value: unknown): AdPlatform {
    return (
        (typeof value === 'string' &&
            CHANNEL_PARAM_TO_AD_PLATFORM[value.toLowerCase()]) ||
        AD_PLATFORM.META
    )
}

export function AIChatPage() {
    const router = useRouter()
    const { state, dispatch } = useAIChatContext()
    const {
        startFlow,
        startManualFlow,
        startSearchQueryFlow,
        startFlowWithSession,
    } = useAIChatOrchestrator()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [initialAd, setInitialAd] = useState<MetaAd | null>(null)
    const [initialAdChannel, setInitialAdChannel] = useState<AdPlatform>(
        AD_PLATFORM.META,
    )
    // Gates the composer's first mount so it can take initialAd as a plain
    // initial value instead of syncing it in after an async resolution.
    const [isAdPreselectReady, setIsAdPreselectReady] = useState(false)

    const handlePrefaceSubmit = useCallback(
        async (submission: PrefaceSubmission) => {
            dispatch({
                type: 'SET_PREFACE',
                prefaceContext: {
                    channel: submission.channel,
                    userInput: submission.userInput,
                    personaName: submission.personaName,
                    productNames: submission.productNames,
                    referenceUrl: submission.referenceUrl,
                    sourceStorefrontName: submission.sourceStorefrontName,
                    variantCount: submission.variantCount,
                    isManualCreative: !!submission.manualCreative,
                },
            })

            const preselections = {
                personaId: submission.personaId,
                productHashes: submission.productHashes,
                sourceStorefrontHash: submission.sourceStorefrontHash,
            }

            setIsSubmitting(true)
            try {
                if (submission.ad) {
                    await startFlow(
                        submission.ad,
                        submission.channel,
                        submission.variantCount,
                        submission.userInput,
                        preselections,
                        submission.referenceUrl,
                    )
                } else if (submission.searchQuery) {
                    await startSearchQueryFlow(
                        submission.searchQuery.searchQuery,
                        submission.searchQuery.currentLandingPageUrl,
                        submission.channel,
                        submission.variantCount,
                        submission.userInput,
                        preselections,
                        submission.referenceUrl,
                    )
                } else {
                    await startManualFlow(
                        submission.manualCreative?.assets ?? [],
                        submission.manualCreative?.adCopy,
                        submission.manualCreative?.productUrl,
                        submission.manualCreative?.creativeName,
                        submission.channel,
                        submission.variantCount,
                        submission.userInput,
                        preselections,
                        submission.referenceUrl,
                    )
                }
            } finally {
                setIsSubmitting(false)
            }
        },
        [dispatch, startFlow, startManualFlow, startSearchQueryFlow],
    )

    // Resume a session created elsewhere (e.g. the email/SMS entry) when handed
    // off via ?sessionId. Fires once, after the router has hydrated the query.
    const resumedRef = useRef(false)
    useEffect(() => {
        if (!router.isReady || resumedRef.current) return
        const { sessionId } = router.query
        if (typeof sessionId === 'string' && sessionId) {
            resumedRef.current = true
            void startFlowWithSession(sessionId)
        }
    }, [router.isReady, router.query, startFlowWithSession])

    const adResolvedRef = useRef(false)
    useEffect(() => {
        if (!router.isReady || adResolvedRef.current) return
        adResolvedRef.current = true
        const { creativeId, channel: channelParam } = router.query
        const channel = parseChannelParam(channelParam)
        setInitialAdChannel(channel)
        if (
            typeof creativeId !== 'string' ||
            !creativeId ||
            channel !== AD_PLATFORM.META
        ) {
            setIsAdPreselectReady(true)
            return
        }
        R.getMetaAds()
            .then((ads) => {
                const match = ads.find(
                    (a) => a.id === creativeId || a.adId === creativeId,
                )
                if (match) {
                    setInitialAd(match)
                } else {
                    toast.error(
                        'We couldn’t find this ad automatically. Try searching for it instead.',
                    )
                }
            })
            .catch(() => {
                toast.error(
                    'We couldn’t find this ad automatically. Try searching for it instead.',
                )
            })
            .finally(() => setIsAdPreselectReady(true))
    }, [router.isReady, router.query])

    // "Create Variant" hands off via ?sourceStorefront=<hash>. The seed is the
    // hash straight from the URL, so the composer mounts pinned without waiting
    // on a lookup; the storefront's name fills into the pill when it arrives.
    const sourceStorefrontSeed = useSourceStorefrontSeed()

    return (
        <div className="flex h-full flex-col">
            {state.error && (
                <div className="border-destructive/40 bg-destructive/5 mx-auto mt-3 flex w-full max-w-3xl items-start gap-2 rounded-lg border px-3 py-2 text-sm text-destructive">
                    <AlertCircle className="mt-0.5 size-4 shrink-0" />
                    {state.error}
                </div>
            )}

            {/* Content. The chat holds the whole stage until a storefront
                exists; the preview pane slides in beside it once it does. */}
            {!state.sessionId ? (
                isAdPreselectReady && (
                    <PrefaceComposer
                        onSubmit={handlePrefaceSubmit}
                        isSubmitting={isSubmitting}
                        initialAd={initialAd}
                        initialAdChannel={initialAdChannel}
                        initialSourceStorefront={sourceStorefrontSeed}
                    />
                )
            ) : (
                <div className="flex-1 overflow-hidden bg-chat-surface">
                    <AICreateRuntimeProvider
                        key={state.sessionId}
                        sessionId={state.sessionId}
                    >
                        <AiCreateRunSignalsProvider>
                            <AiCreateSessionFeed />
                            {/* The chat panel must never remount when the
                                preview arrives — the thread would lose scroll
                                and collapsibles mid-run. */}
                            <AiCreateChatLayout />
                        </AiCreateRunSignalsProvider>
                    </AICreateRuntimeProvider>
                </div>
            )}
        </div>
    )
}
